/**
 * Passwords must reach Supabase exactly as typed.
 *
 * Until 2026-10 the sign-in and sign-up forms ran the same "sanitizer" as the
 * display-name field on every password keystroke: trim plus strip `< > " ' &`.
 * The password-recovery dialog never did, so anyone who reset to a password
 * containing one of those characters could never sign in again — the form sent
 * `Summer2026` for `Summer&2026` and Supabase said "invalid credentials".
 *
 * Accounts created by the old sign-up form, however, were stored with the
 * stripped password. Those users still type the original, so a sign-in that
 * fails with the raw password is retried once with the legacy transformation.
 */
const LEGACY_STRIP = /[<>"'&]/g;

/**
 * The password the old form would have sent, or null when stripping changes
 * nothing (no point retrying with an identical value).
 */
export function legacyStrippedPassword(password: string): string | null {
  const stripped = password.trim().replace(LEGACY_STRIP, "");
  return stripped !== password ? stripped : null;
}

export type PasswordSignIn = (credentials: {
  email: string;
  password: string;
}) => Promise<{ error: { message: string } | null }>;

export function isInvalidCredentials(error: { message: string } | null): boolean {
  return Boolean(error?.message.includes("Invalid login credentials"));
}

/**
 * Sign in with the password as typed; on "invalid credentials" fall back once
 * to the legacy-stripped form for accounts created under the old sign-up.
 */
export async function signInWithPasswordCompat(
  signIn: PasswordSignIn,
  email: string,
  password: string,
): Promise<{ error: { message: string } | null; usedLegacyPassword: boolean }> {
  const first = await signIn({ email, password });
  if (!isInvalidCredentials(first.error)) {
    return { error: first.error, usedLegacyPassword: false };
  }

  const legacy = legacyStrippedPassword(password);
  if (legacy === null) return { error: first.error, usedLegacyPassword: false };

  const second = await signIn({ email, password: legacy });
  if (second.error) return { error: first.error, usedLegacyPassword: false };
  return { error: null, usedLegacyPassword: true };
}
